<template>
    <v-dialog :model-value="modelValue" max-width="400" @update:model-value="$emit('update:modelValue', $event)">
        <v-card rounded="lg" class="pa-2">
            <v-card-title class="d-flex justify-space-between align-center">
                <span class="text-h6">Join a circle</span>
                <v-btn icon="mdi-close" size="small" variant="text" @click="close"/>
            </v-card-title>
            <v-card-text>
                <p class="text-body-2 text-medium-emphasis mb-3">
                    Enter the circle code from the owner (like XXX-XXX). They'll get a request to approve.
                </p>
                <div class="d-flex align-center justify-center ga-2 mb-2">
                    <v-text-field v-model="part1" maxlength="3" hide-details density="comfortable"
                                  variant="outlined" class="code-segment"
                                  @update:model-value="(v) => onSegment(1, v)"/>
                    <span class="text-h5 font-weight-bold">-</span>
                    <v-text-field v-model="part2" maxlength="3" hide-details density="comfortable"
                                  variant="outlined" class="code-segment"
                                  @update:model-value="(v) => onSegment(2, v)"/>
                </div>
                <v-alert v-if="foundName" type="success" variant="tonal" density="compact" class="mb-1">
                    Found: <strong>{{ foundName }}</strong>
                </v-alert>
                <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mb-1">
                    {{ error }}
                </v-alert>
            </v-card-text>
            <v-card-actions class="px-4 pb-4">
                <v-spacer/>
                <v-btn variant="text" @click="close">Cancel</v-btn>
                <v-btn color="primary" :loading="joining" :disabled="code.length !== 6" @click="join">
                    Send request
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {computed, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";

defineProps({modelValue: Boolean});
const emit = defineEmits(['update:modelValue']);
const {showSnackbar} = useSnackbarStore();

const part1 = ref("");
const part2 = ref("");
const code = computed(() => part1.value + part2.value);
const foundName = ref("");
const error = ref("");
const joining = ref(false);

// Each box keeps 3 A-Z/0-9 chars, uppercased. Preview the circle once 6 are in.
const onSegment = (n, val) => {
    const clean = (val || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 3);
    if (n === 1) part1.value = clean; else part2.value = clean;
    foundName.value = "";
    error.value = "";
    if (code.value.length === 6) lookup();
};
const lookup = () => {
    send('get', `circle/lookup?code=${code.value}`)
        .then((response) => {
            foundName.value = response.data?.data?.company_name ?? "";
        })
        .catch(() => {
            error.value = "No circle found for that code.";
        });
};
const join = () => {
    if (code.value.length !== 6) return;
    joining.value = true;
    send('post', 'circle/join', {code: code.value})
        .then((response) => {
            if (response.data.success) {
                showSnackbar(response.data.message);
                close();
            }
        })
        .catch((err) => {
            error.value = err.response?.data?.message ?? 'Something went wrong';
        })
        .finally(() => joining.value = false);
};
const close = () => {
    part1.value = "";
    part2.value = "";
    foundName.value = "";
    error.value = "";
    emit('update:modelValue', false);
};
</script>

<style scoped>
.code-segment {
    max-width: 96px;
}

.code-segment :deep(input) {
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 4px;
    font-weight: 700;
}
</style>
